import { RECOM_ERROR_CODE } from '@/data/recom/constants';

export type RecomJsonApiError = {
	status?: string;
	code?: string;
	title?: string;
	detail?: string;
	meta?: {
		input?: unknown;
		parameters?: Record<string, unknown>;
		diagnosticInformation?: {
			upstreamStatusCode?: number;
		};
	};
};

export type RecomErrorPayload = {
	errors?: RecomJsonApiError[];
};

export type RecomApiError = Error & {
	name: 'RecomApiError';
	status: number;
	code: string;
	detail: string;
	upstreamStatusCode?: number;
	payload?: unknown;
};

export const isRecomApiError = (error: unknown): error is RecomApiError =>
	error instanceof Error && error.name === 'RecomApiError';

export const createRecomApiError = ({
	status,
	code,
	detail,
	upstreamStatusCode,
	payload,
}: {
	status: number;
	code: string;
	detail: string;
	upstreamStatusCode?: number;
	payload?: unknown;
}): RecomApiError =>
	Object.assign(new Error(`[${code}] ${detail}`), {
		name: 'RecomApiError' as const,
		status,
		code,
		detail,
		upstreamStatusCode,
		payload,
	});

const firstError = (payload: unknown): RecomJsonApiError | undefined => {
	const errors = (payload as RecomErrorPayload | undefined)?.errors;
	return Array.isArray(errors) ? errors[0] : undefined;
};

export const toRecomApiError = (status: number, payload: unknown): RecomApiError => {
	const error = firstError(payload);
	return createRecomApiError({
		status,
		code: error?.code ?? RECOM_ERROR_CODE.internal,
		detail: error?.detail ?? error?.title ?? `Request failed with status ${status}`,
		upstreamStatusCode: error?.meta?.diagnosticInformation?.upstreamStatusCode,
		payload,
	});
};

export const recomErrorPayload = (
	status: number,
	code: string,
	detail: string
): Required<RecomErrorPayload> => ({
	errors: [{ status: String(status), code, title: code, detail }],
});
